import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { BrandIcon } from "@/components/BrandIcon";

const accentMap = {
  green: { border: "rgba(0,255,136,0.28)", glow: "0 0 44px rgba(0,255,136,0.22)", text: "#00ff88" },
  orange: { border: "rgba(255,153,0,0.30)", glow: "0 0 44px rgba(255,153,0,0.22)", text: "#ff9900" },
  red: { border: "rgba(255,0,0,0.32)", glow: "0 0 44px rgba(255,0,0,0.28)", text: "#ff5555" },
};

const MotionLink = motion.create(Link);

export const TiltCard = ({ item, index }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 200, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 200, damping: 18 });

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  const a = accentMap[item.accent] || accentMap.green;

  const isInternal = item.internal;
  const Wrapper = isInternal ? MotionLink : motion.a;
  const linkProps = isInternal
    ? { to: item.url }
    : { href: item.url, target: "_blank", rel: "noopener noreferrer" };

  return (
    <Wrapper
      ref={ref}
      {...linkProps}
      data-testid={`social-card-${item.key}`}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-8% 0px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.07, ease: [0.16, 1, 0.3, 1] }}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 900, borderColor: a.border }}
      whileHover={{ boxShadow: a.glow }}
      className="glass group relative flex min-h-[230px] flex-col justify-between overflow-hidden border p-8 no-underline"
    >
      {/* Giant ghost icon */}
      <BrandIcon
        name={item.key}
        className="pointer-events-none absolute -right-6 -bottom-8 text-[150px] opacity-[0.05] transition-opacity duration-500 group-hover:opacity-[0.11]"
      />

      <div className="relative z-10 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <span
            className="flex h-11 w-11 items-center justify-center rounded-full border transition-all duration-300"
            style={{ borderColor: a.border, color: a.text }}
          >
            <BrandIcon name={item.key} className="text-lg" />
          </span>
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">
            {item.category}
          </span>
        </div>
        <ArrowUpRight
          className="h-5 w-5 -translate-x-1 translate-y-1 opacity-40 transition-all duration-300 group-hover:translate-x-0 group-hover:translate-y-0 group-hover:opacity-100"
          style={{ color: a.text }}
        />
      </div>

      <div className="relative z-10">
        <h3 className="font-display text-2xl font-bold tracking-tight text-white">{item.label}</h3>
        {item.description ? (
          <p className="mt-2 max-w-[92%] font-mono text-[13px] leading-relaxed text-white/60">
            {item.description}
          </p>
        ) : (
          <p className="mt-1 font-mono text-sm" style={{ color: a.text }}>{item.handle}</p>
        )}

        {item.cta ? (
          <span
            className="mt-5 inline-flex items-center gap-2 rounded-full border px-4 py-2 font-display text-xs font-bold uppercase tracking-wider transition-all duration-300 group-hover:gap-3"
            style={{ borderColor: a.border, color: a.text }}
          >
            {item.cta}
            <span aria-hidden>&rarr;</span>
          </span>
        ) : null}
      </div>

      <span
        className="absolute bottom-0 left-0 h-[2px] w-0 transition-all duration-500 group-hover:w-full"
        style={{ background: a.text, boxShadow: a.glow }}
      />
    </Wrapper>
  );
};
